/**
 * 站内通知 / 公告 API (B-8)
 *
 * 后端:
 * - GET  /app/notify/inbox/list                  我的通知分页
 * - GET  /app/notify/inbox/unread-count          未读数 (number)
 * - PUT  /app/notify/inbox/{id}/read             单条标已读
 * - PUT  /app/notify/inbox/read                  全部标已读
 * - GET  /app/notify/wechat-mp/status            服务号绑定 / 关注状态
 * - POST /app/notify/wechat-mp/bind-session      创建服务号关注入口
 */
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { apiBaseURL, http } from '@/lib/http'
import { toNumber } from '@/lib/utils'
import type {
  LegalNotificationVo,
  NotifyWechatMpBindSessionVo,
  NotifyWechatMpStatusVo,
  PageQuery,
  TableDataInfo,
} from '@/types/api'

export const CONSULT_MESSAGE_NOTIFY_BIZ_CODE =
  import.meta.env.VITE_CONSULT_NOTIFY_BIZ_CODE?.trim() || 'consult_chat_offline'

const KEYS = {
  list: ['notification', 'my'] as const,
  announcements: ['notification', 'announcements'] as const,
  unread: ['notification', 'unread-count'] as const,
  wechatMpStatus: (bizCode: string, channelConfigId?: string) =>
    ['notification', 'wechat-mp', 'status', bizCode, channelConfigId ?? 'default'] as const,
}

interface WechatMpNotifyInput {
  bizCode?: string
  channelConfigId?: string
}

type WechatMpStatusRefetchInterval = UseQueryOptions<NotifyWechatMpStatusVo>['refetchInterval']

/** 将后端返回的绑定入口转换成可被微信扫码访问的绝对地址 */
export function resolveWechatMpBindUrl(webViewUrl: string | null | undefined): string | undefined {
  const normalized = normalizeOptionalString(webViewUrl)
  if (!normalized) return undefined
  if (/^[a-z][a-z\d+.-]*:/i.test(normalized)) return normalized

  const path = normalized.startsWith('/') ? normalized : `/${normalized}`
  const apiPath = resolveApiBasePath()
  const pathWithApiPrefix =
    apiPath && apiPath !== '/' && !path.startsWith(`${apiPath}/`) && path !== apiPath
      ? `${apiPath}${path}`
      : path

  if (typeof window === 'undefined') return pathWithApiPrefix
  return new URL(pathWithApiPrefix, resolveApiOrigin()).toString()
}

/** 未读数 (Sidebar / Settings 显示用) */
export function useUnreadCount(opts: { enabled?: boolean; refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: KEYS.unread,
    queryFn: async () =>
      toNumber(await http.get<string | number>('/app/notify/inbox/unread-count')),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: opts.refetchInterval,
  })
}

/** 公众号离线提醒绑定 / 关注状态 */
export function useWechatMpStatus(
  input: WechatMpNotifyInput = {},
  opts: { enabled?: boolean; refetchInterval?: WechatMpStatusRefetchInterval } = {},
) {
  const bizCode = input.bizCode ?? CONSULT_MESSAGE_NOTIFY_BIZ_CODE
  return useQuery({
    queryKey: KEYS.wechatMpStatus(bizCode, input.channelConfigId),
    queryFn: () =>
      http.get<NotifyWechatMpStatusVo>('/app/notify/wechat-mp/status', {
        params: {
          bizCode,
          ...(input.channelConfigId ? { channelConfigId: input.channelConfigId } : {}),
        },
      }),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
    refetchInterval: opts.refetchInterval,
  })
}

/** 创建公众号绑定会话, 用于展示服务号二维码 / 授权入口 */
export function useCreateWechatMpBindSession(
  input: Pick<WechatMpNotifyInput, 'channelConfigId'> & { returnTo?: 'web' } = {},
) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: [
      'notification',
      'wechat-mp',
      'bind-session',
      input.channelConfigId ?? 'default',
      input.returnTo,
    ],
    mutationFn: async () =>
      http.post<NotifyWechatMpBindSessionVo>('/app/notify/wechat-mp/bind-session', undefined, {
        params: {
          ...(input.channelConfigId ? { channelConfigId: input.channelConfigId } : {}),
          ...(input.returnTo ? { returnTo: input.returnTo } : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notification', 'wechat-mp', 'status'] })
    },
  })
}

/** 我的通知分页 */
export function useMyNotifications(page: PageQuery = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEYS.list, page],
    queryFn: () =>
      http.get<TableDataInfo<LegalNotificationVo>>('/app/notify/inbox/list', {
        params: page,
      }),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  })
}

/** 平台公告分页 */
export function useAnnouncements(page: PageQuery = {}, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEYS.announcements, page],
    queryFn: () =>
      http.get<TableDataInfo<LegalNotificationVo>>('/app/notify/inbox/list', {
        params: page,
      }),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
  })
}

/** 单条标已读 */
export function useReadNotification() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['notification', 'read'],
    mutationFn: async (notificationId: string | number) =>
      http.put<void>(`/app/notify/inbox/${encodeURIComponent(String(notificationId))}/read`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list })
      qc.invalidateQueries({ queryKey: KEYS.unread })
    },
  })
}

/** 全部标已读 */
export function useReadAllNotifications() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['notification', 'read-all'],
    mutationFn: async () => http.put<void>('/app/notify/inbox/read', { ids: [] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list })
      qc.invalidateQueries({ queryKey: KEYS.unread })
    },
  })
}

function resolveApiOrigin(): string {
  if (typeof window === 'undefined') return ''
  try {
    return new URL(apiBaseURL, window.location.origin).origin
  } catch {
    return window.location.origin
  }
}

function resolveApiBasePath(): string {
  if (typeof window === 'undefined') return ''
  try {
    const pathname = new URL(apiBaseURL, window.location.origin).pathname.replace(/\/$/, '')
    return pathname === '/' ? '' : pathname
  } catch {
    return ''
  }
}

function normalizeOptionalString(value: string | null | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized || undefined
}
